/**
 * Cards Integration (shared) - single source for banner lifecycle, carousel
 * state, badge handling, gestures, and show/hide orchestration.
 * Specific flows (spots, tags/explore, maps) should build on top of this file.
 */

import { LOGGING, CARDS_SETTINGS, MAP_ANIMATION_SETTINGS } from '/settings.js';
import { getDistanceMeters, formatDistance } from '../utils/location-utils.js';
import { getText } from '../translations/translations.js';
import { renderCard, getBadgeHtmlForCard, createBadgeUpdateFunction, renderTabs, buildContainerHtml } from './cards-design.js';
import { getCardsSettings } from './cards-settings.js';
import { enableCarouselNavigation } from '../utils/gesture-utils.js';
import { createIconMarkup } from '../utils/icon.js';
import { isFavorite } from '../utils/favorites.js';
import { pauseMarkerListeners, resumeMarkerListeners } from '../marker/marker.js';

let cardsBannerContainer = null;
const carouselState = new Map();
const gestureCleanupFunctions = new Map();
const gestureSetupPending = new Set();
const swipeHintCleanupFunctions = new Map();
let hideBannerTimeout = null;
let badgeSuffixProvider = () => '';
let currentBannerType = null;
const loadTokens = new Map(); // track latest load per carouselId
let activeCarouselId = null;
const SWIPE_HINT_STORAGE_KEY = 'cards_has_swiped';

function logCards(message, data = null) {
    if (LOGGING.ENABLED && LOGGING.ADS) {
        if (data) {
            console.log(`[CARDS] ${message}`, data);
        } else {
            console.log(`[CARDS] ${message}`);
        }
    }
}

function clearHideTimeout() {
    if (hideBannerTimeout) {
        clearTimeout(hideBannerTimeout);
        hideBannerTimeout = null;
    }
}

export function setBadgeSuffixProvider(provider) {
    badgeSuffixProvider = typeof provider === 'function' ? provider : () => '';
}

export function setCurrentBannerType(type) {
    currentBannerType = type || null;
}

export function getCurrentBannerType() {
    return currentBannerType;
}

export function getBadgeSuffix() {
    try {
        return badgeSuffixProvider(currentBannerType) || '';
    } catch (error) {
        console.warn('Badge suffix provider failed:', error);
        return '';
    }
}

function hasSeenSwipeHint() {
    try {
        return sessionStorage.getItem(SWIPE_HINT_STORAGE_KEY) === 'true';
    } catch (error) {
        console.warn('Could not read swipe hint flag from sessionStorage:', error);
        return false;
    }
}

function markSwipeHintSeen() {
    try {
        sessionStorage.setItem(SWIPE_HINT_STORAGE_KEY, 'true');
    } catch (error) {
        console.warn('Could not persist swipe hint flag to sessionStorage:', error);
    }
}

function getSwipeHintText() {
    try {
        const text = typeof getText === 'function' ? getText('cards.swipeHint') : null;
        return text || 'SWIPE FOR MORE';
    } catch (error) {
        console.warn('Could not get swipe hint text:', error);
        return 'SWIPE FOR MORE';
    }
}

function fadeOutSwipeHint(hintEl) {
    if (!hintEl) return;
    hintEl.classList.add('hide');
    setTimeout(() => {
        hintEl.remove();
    }, 300);
}

function markSwipeHintSeenForCarousel(carouselEl) {
    if (!carouselEl) return;
    const hintEl = carouselEl.querySelector('.carousel-swipe-hint');
    if (!hintEl) return;
    markSwipeHintSeen();
    fadeOutSwipeHint(hintEl);
}

function setupSwipeHintDismissal(carouselId) {
    const previousCleanup = swipeHintCleanupFunctions.get(carouselId);
    if (previousCleanup) {
        previousCleanup();
    }
    const container = document.getElementById(carouselId);
    if (!container) return;

    const hintEl = container.querySelector('.carousel-swipe-hint');
    if (!hintEl) return;

    let startX = null;
    let startY = null;
    const threshold = 12;

    const handleTouchStart = event => {
        const touch = event.touches[0];
        if (!touch) return;
        startX = touch.clientX;
        startY = touch.clientY;
    };

    const handleTouchMove = event => {
        if (startX === null || startY === null) return;
        const touch = event.touches[0];
        if (!touch) return;
        const deltaX = Math.abs(touch.clientX - startX);
        const deltaY = Math.abs(touch.clientY - startY);
        if (deltaX > threshold || deltaY > threshold) {
            markSwipeHintSeenForCarousel(container);
            cleanup();
        }
    };

    const cleanup = () => {
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        swipeHintCleanupFunctions.delete(carouselId);
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    swipeHintCleanupFunctions.set(carouselId, cleanup);
}

// Expose to window for cards-design.js and others
window.getBadgeSuffix = getBadgeSuffix;

/**
 * Global tab selection handler
 * @param {string} tabId - The ID of the clicked tab
 */
window.__cardsTabSelect = async function (tabId) {
    const banner = document.getElementById('spots-cards-banner');
    if (!banner) return;
    const carousel = banner.querySelector('.cards-carousel-container');
    if (!carousel) return;
    const carouselId = carousel.id;
    const state = carouselState.get(carouselId);
    if (!state) return;

    // 1. Find the first card with this tag_id or matching the tabId
    const targetIndex = state.cards.findIndex(c => String(c.tag_id || c.id) === String(tabId));

    if (targetIndex !== -1) {
        await goToCardSlide(carouselId, targetIndex);
    }

    // 2. Update active tab UI
    const tabs = banner.querySelectorAll('.cards-tab');
    tabs.forEach(t => {
        const isTarget = t.getAttribute('onclick')?.includes(`'${tabId}'`);
        t.classList.toggle('active', isTarget);
    });
};

export function ensureCardsContainer() {
    // Container now always exists in HTML - just get reference
    if (!cardsBannerContainer) {
        cardsBannerContainer = document.getElementById('spots-cards-banner');
    }
    return cardsBannerContainer;
}

export function getCardsContainer() {
    return cardsBannerContainer || document.getElementById('spots-cards-banner');
}

/**
 * Hides the cards banner or swaps to empty content based on mode rules
 * 
 * If banner should always be visible (per hierarchy rules), this function
 * swaps to empty content instead of hiding. Otherwise, it hides the banner.
 * 
 * @param {string} [mode] - Optional mode override (defaults to current mode from hierarchy)
 */
export async function hideCardsBanner(mode = null) {
    const container = getCardsContainer();
    if (!container) return;

    clearHideTimeout();

    // Check if banner should always be visible using hierarchy system
    let shouldAlwaysBeVisible = false;
    try {
        const { getCurrentMode, isBannerAlwaysVisible } = await import('./cards-hierarchy.js');
        const currentMode = mode || getCurrentMode();
        shouldAlwaysBeVisible = isBannerAlwaysVisible(currentMode);
    } catch (error) {
        // If hierarchy system not available, default to hiding
        console.warn('[CardsIntegration] Could not check banner visibility rules:', error);
    }

    // NEW: If banner must stay visible, leave current content intact to avoid disappearing.
    if (!shouldAlwaysBeVisible) {
        // Banner can be hidden - use soft hide (keep container mounted)
        // IMPORTANT: Never clear gesture handlers here - keep them active for the bottom cards area
        // Gestures will be managed by setupCarouselGestures which handles cleanup/re-initialization
        hideBannerTimeout = setTimeout(() => {
            hideBannerTimeout = null;
        }, 100);
    }
}

/**
 * Swaps banner content to empty/placeholder state
 * Keeps banner visible but shows no cards
 * 
 * @param {HTMLElement} container - Banner container element
 */
async function swapToEmptyContent(container) {
    if (!container) return;

    await fadeSwapContent(() => {
        // Clear current content but keep banner structure
        container.innerHTML = '';

        // Ensure banner stays visible
        container.classList.add('show');

        // Clear active carousel ID
        activeCarouselId = null;

        // Clear current banner type
        setCurrentBannerType(null);
    });

    if (window.location.hostname === 'localhost') {
        logCards('Swapped to empty content (banner always visible mode)');
    }
}

export function getInitialCardIndex(storageKey, cardsLength) {
    let initialIndex = 0;
    try {
        const savedIndex = sessionStorage.getItem(storageKey);
        if (savedIndex !== null) {
            const parsedIndex = parseInt(savedIndex, 10);
            if (!isNaN(parsedIndex) && parsedIndex >= 0 && parsedIndex < cardsLength) {
                initialIndex = parsedIndex;
            }
        }
    } catch (error) {
        console.warn('Could not read last card index from sessionStorage:', error);
    }
    return initialIndex;
}

export function saveCardIndexToStorage(carouselId, currentIndex) {
    const storageKey = `cards_last_index_${carouselId}`;
    try {
        sessionStorage.setItem(storageKey, currentIndex.toString());
    } catch (error) {
        console.warn('Could not save last card index to sessionStorage:', error);
    }
}




export function computeInitialBadgeHtml(cards, storageKey, bannerDisplay) {
    if (!cards.length) return { html: '', badgeKey: null };
    const initialIndex = getInitialCardIndex(storageKey, cards.length);
    const initialCard = cards[initialIndex];
    const badgeKey = initialCard?.badge ? String(initialCard.badge).trim().toLowerCase() : null;
    const html = getBadgeHtmlForCard(initialCard, bannerDisplay);
    return { html, badgeKey };
}

export async function createCardsCarousel(cards, carouselId) {
    if (!cards || cards.length === 0) {
        return '';
    }

    const storageKey = `cards_last_index_${carouselId}`;
    const initialIndex = getInitialCardIndex(storageKey, cards.length);

    carouselState.set(carouselId, { cards, currentIndex: initialIndex });

    const initialCardHtml = await renderCard(cards[initialIndex]);
    if (!initialCardHtml) {
        return '';
    }

    const isSingleCard = cards.length === 1;
    const shouldShowSwipeHint = !isSingleCard && !hasSeenSwipeHint();
    const swipeHintHtml = shouldShowSwipeHint ? `<div class="carousel-swipe-hint">${getSwipeHintText()}</div>` : '';
    let carouselHtml = `<div class="cards-carousel-container" id="${carouselId}">`;

    carouselHtml += `<div class="cards-carousel">`;
    carouselHtml += `<button class="carousel-arrow cards-arrow left ${isSingleCard ? 'hidden' : ''}" onclick="navigateCardsCarousel('${carouselId}', -1)"><svg width="24" height="24" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M28 10L16 22L28 34" stroke="currentColor" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>`;
    carouselHtml += `<div class="carousel-banner">${initialCardHtml || ''}${swipeHintHtml}</div>`;
    carouselHtml += `<button class="carousel-arrow cards-arrow right ${isSingleCard ? 'hidden' : ''}" onclick="navigateCardsCarousel('${carouselId}', 1)"><svg width="24" height="24" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 10L28 22L16 34" stroke="currentColor" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>`;
    carouselHtml += '</div>';

    carouselHtml += `<div class="card-bottom ${isSingleCard ? 'hidden' : ''}"><div class="carousel-dots">`;
    for (let i = 0; i < cards.length; i++) {
        const slideNumber = i + 1;
        const totalSlides = cards.length;
        carouselHtml += `<button class="carousel-dot ${i === initialIndex ? 'active' : ''}" onclick="goToCardSlide('${carouselId}', ${i})" aria-label="Go to slide ${slideNumber} of ${totalSlides}"></button>`;
    }
    carouselHtml += '</div></div>';

    // swipe hint is rendered inside the banner for initial load
    carouselHtml += '</div>';

    return carouselHtml;
}

export function updateCarouselDots(carousel, activeIndex) {
    let dotsContainer = carousel.querySelector('.carousel-dots');
    if (!dotsContainer) {
        const banner = document.getElementById('spots-cards-banner');
        if (banner) {
            dotsContainer = banner.querySelector('.carousel-dots');
        }
    }
    if (dotsContainer) {
        const dots = dotsContainer.querySelectorAll('.carousel-dot');
        if (dots.length) {
            const totalSlides = dots.length;
            dots.forEach((dot, i) => {
                // Update aria-label for accessibility
                const slideNumber = i + 1;
                dot.setAttribute('aria-label', `Go to slide ${slideNumber} of ${totalSlides}`);
                if (i === activeIndex) {
                    dot.classList.add('active');
                    dot.setAttribute('aria-current', 'true');
                } else {
                    dot.classList.remove('active');
                    dot.removeAttribute('aria-current');
                }
            });
        }
    }
}

export function getCarouselState(carouselId = null) {
    if (carouselId) {
        return carouselState.get(carouselId);
    }
    const banner = document.getElementById('spots-cards-banner');
    if (!banner) return null;
    const carousel = banner.querySelector('.cards-carousel-container');
    if (carousel && carousel.id) {
        return carouselState.get(carousel.id) || null;
    }
    return null;
}

/**
 * Helper to perform a fade transition when swapping content
 * @param {Function} updateCallback - Function that performs the DOM updates
 * @returns {Promise<void>}
 */
async function fadeSwapContent(updateCallback) {
    const container = getCardsContainer();
    if (!container) {
        await updateCallback();
        return;
    }

    // SOURCE OF TRUTH: Use Map Animation Settings + extra cards duration from settings
    const extraFade = MAP_ANIMATION_SETTINGS?.EXTRA_FADE_FOR_CARDS || 0;
    const duration = (MAP_ANIMATION_SETTINGS?.FADE_DURATION_MS || 300) + extraFade;

    if (!isFadeEnabled) {
        await updateCallback();
        return;
    }

    // 1. Start fade out immediately
    container.style.transitionDuration = duration + 'ms';
    container.classList.remove('show');
    // Dispatch event for control visibility management
    window.dispatchEvent(new Event('cardBannerHide'));

    // 2. Wait for fade out to complete
    await new Promise(resolve => setTimeout(resolve, duration));

    // 3. Update content while hidden
    await updateCallback();

    // 4. Trigger reflow to ensure hidden state is processed
    void container.offsetHeight;

    // 5. Start fade in
    container.classList.add('show');

    // 6. Cleanup transition duration after fade in finishes
    setTimeout(() => {
        if (container) container.style.transitionDuration = '';
    }, duration + 50);
}

export async function navigateCardsCarousel(carouselId, direction) {
    const carousel = document.getElementById(carouselId);
    if (!carousel) return;

    markSwipeHintSeenForCarousel(carousel);

    const state = carouselState.get(carouselId);
    if (!state) return;
    const prevIndex = state.currentIndex;
    const len = state.cards.length;
    if (LOGGING?.ENABLED && (LOGGING?.DEBUG || LOGGING?.GENERAL)) {
        console.log('[CARDS] navigateCardsCarousel', { carouselId, direction, prevIndex, len });
    }
    state.currentIndex = (state.currentIndex + direction + len) % len;

    const container = carousel.querySelector('.carousel-banner');
    if (container) {
        const cardHtml = await renderCard(state.cards[state.currentIndex]);
        container.innerHTML = cardHtml || '';
    }

    if (window.updateCardBadge) {
        window.updateCardBadge(state.cards[state.currentIndex]);
    }

    updateCarouselDots(carousel, state.currentIndex);
    saveCardIndexToStorage(carouselId, state.currentIndex);
    if (LOGGING?.ENABLED && (LOGGING?.DEBUG || LOGGING?.GENERAL)) {
        console.log('[CARDS] navigateCardsCarousel: updated index', { currentIndex: state.currentIndex });
    }
}

export async function goToCardSlide(carouselId, slideIndex) {
    const carousel = document.getElementById(carouselId);
    if (!carousel) return;

    markSwipeHintSeenForCarousel(carousel);

    const state = carouselState.get(carouselId);
    if (!state) return;

    if (slideIndex < 0 || slideIndex >= state.cards.length) return;
    const prevIndex = state.currentIndex;
    if (LOGGING?.ENABLED && (LOGGING?.DEBUG || LOGGING?.GENERAL)) {
        console.log('[CARDS] goToCardSlide', { carouselId, slideIndex, prevIndex, len: state.cards.length });
    }
    state.currentIndex = slideIndex;

    const container = carousel.querySelector('.carousel-banner');
    if (container) {
        const cardHtml = await renderCard(state.cards[state.currentIndex]);
        container.innerHTML = cardHtml || '';
    }

    if (window.updateCardBadge) {
        window.updateCardBadge(state.cards[state.currentIndex]);
    }

    updateCarouselDots(carousel, state.currentIndex);
    saveCardIndexToStorage(carouselId, state.currentIndex);
    if (LOGGING?.ENABLED && (LOGGING?.DEBUG || LOGGING?.GENERAL)) {
        console.log('[CARDS] goToCardSlide: updated index', { currentIndex: state.currentIndex });
    }
}

// Expose navigation for inline onclick handlers
window.navigateCardsCarousel = navigateCardsCarousel;
window.goToCardSlide = goToCardSlide;

export function setupCarouselGestures(carouselId) {
    const existingCleanup = gestureCleanupFunctions.get(carouselId);
    if (existingCleanup) {
        existingCleanup();
    }

    // Prevent overlapping setup calls that attach duplicate listeners
    if (gestureSetupPending.has(carouselId)) {
        if (LOGGING?.ENABLED && (LOGGING?.DEBUG || LOGGING?.GENERAL)) {
            console.log('[CARDS] setupCarouselGestures: setup already pending', { carouselId });
        }
        return;
    }
    gestureSetupPending.add(carouselId);

    requestAnimationFrame(() => {
        const carousel = document.getElementById(carouselId);
        if (!carousel) {
            logCards('Carousel not found for gestures:', carouselId);
            gestureSetupPending.delete(carouselId);
            return;
        }

        const state = carouselState.get(carouselId);
        if (!state || state.cards.length <= 1) {
            return;
        }

        const carouselContainer = carousel.querySelector('.cards-carousel');
        if (!carouselContainer) {
            logCards('Carousel container not found for gestures:', carouselId);
            return;
        }

        const onNavigateLeft = () => {
            window.navigateCardsCarousel(carouselId, -1);
        };

        const onNavigateRight = () => {
            window.navigateCardsCarousel(carouselId, 1);
        };

        const isActive = () => {
            const banner = document.getElementById('spots-cards-banner');
            // Cards banner is active if it has the .show class (CSS-based visibility)
            return banner && banner.classList.contains('show');
        };

        const cleanup = enableCarouselNavigation(
            carouselContainer,
            onNavigateLeft,
            onNavigateRight,
            {
                enableSwipe: true,
                enableKeyboard: true,
                isActive: isActive
            }
        );

        gestureCleanupFunctions.set(carouselId, cleanup);
        gestureSetupPending.delete(carouselId);
        logCards('Gestures enabled for carousel:', carouselId);
    });
}

export function animateBannerOut(duration) {
    const container = getCardsContainer();
    if (container) {
        // SOURCE OF TRUTH: Map duration + extra cards duration from settings
        const extraFade = MAP_ANIMATION_SETTINGS?.EXTRA_FADE_FOR_CARDS || 0;
        const configuredDuration = duration || (MAP_ANIMATION_SETTINGS?.FADE_DURATION_MS || 300) + extraFade;
        container.style.transitionDuration = configuredDuration + 'ms';

        // Handle Animation Type
        const type = MAP_ANIMATION_SETTINGS?.TYPE || 'fade';
        if (type === 'fade') {
            container.classList.add('animation-fade');
        } else {
            container.classList.remove('animation-fade');
        }

        container.classList.remove('show');
        // Dispatch event for control visibility management
        window.dispatchEvent(new Event('cardBannerHide'));
    }
}

export function animateBannerIn(duration) {
    const container = getCardsContainer();
    if (container) {
        // SOURCE OF TRUTH: Map duration + extra cards duration from settings
        const extraFade = MAP_ANIMATION_SETTINGS?.EXTRA_FADE_FOR_CARDS || 0;
        const configuredDuration = duration || (MAP_ANIMATION_SETTINGS?.FADE_DURATION_MS || 300) + extraFade;
        container.style.transitionDuration = configuredDuration + 'ms';

        // Handle Animation Type (ensure class matches current setting)
        const type = MAP_ANIMATION_SETTINGS?.TYPE || 'fade';
        if (type === 'fade') {
            container.classList.add('animation-fade');
        } else {
            container.classList.remove('animation-fade');
        }

        container.classList.add('show');

        // Reset transition duration after animation to restore default smooth behavior
        setTimeout(() => {
            if (container) container.style.transitionDuration = '';
        }, configuredDuration + 50);
    }
}

export async function showCardsBannerShared(config) {
    logCards('showCardsBannerShared called with config:', config);

    setCurrentBannerType(config.type || null);
    setBadgeSuffixProvider(config.badgeSuffixProvider || (() => ''));

    const container = ensureCardsContainer();
    if (!container) {
        logCards('No cards banner container found');
        return;
    }

    // Pause marker listeners during transition to prevent unwanted refocusing
    pauseMarkerListeners();

    // Check if current carousel is already visible (using .show class from CSS)
    const isVisible = container.classList.contains('show');
    if (!config.forceRender && activeCarouselId === config.carouselId && isVisible) {
        logCards('Carousel already active and visible, skipping re-render:', config.carouselId);
        resumeMarkerListeners();  // Resume listeners if bailing out early
        return;
    }

    clearHideTimeout();

    // SOURCE OF TRUTH: Use Map Animation Settings + extra cards duration from settings
    const cardsSettings = await getCardsSettings();
    const extraFade = MAP_ANIMATION_SETTINGS?.EXTRA_FADE_FOR_CARDS || 0;
    const duration = (MAP_ANIMATION_SETTINGS?.FADE_DURATION_MS || 300) + extraFade;

    // START FADE OUT IMMEDIATELY if animating and visible
    // This ensures cards and map start fading at the exact same time
    let fadeOutPromise = Promise.resolve();
    if (isVisible && config.animate) {
        container.style.transitionDuration = duration + 'ms';
        container.classList.remove('show');
        fadeOutPromise = new Promise(resolve => setTimeout(resolve, duration));
    }

    // Apply animation type class
    const animType = MAP_ANIMATION_SETTINGS?.TYPE || 'fade';
    if (animType === 'fade') {
        container.classList.add('animation-fade');
    } else {
        container.classList.remove('animation-fade');
    }

    // Track load token to avoid race flashes
    const token = Symbol('cards-load');
    loadTokens.set(config.carouselId, token);

    try {
        const cards = await config.loadCards();
        logCards('Cards loaded:', cards);

        // Abort if a newer load started
        if (loadTokens.get(config.carouselId) !== token) {
            logCards('Discarding stale cards load for', config.carouselId);
            resumeMarkerListeners();  // Resume listeners when aborting
            return;
        }

        if (!cards || cards.length === 0) {
            logCards('No cards found');
            if (config.onNoCards) {
                try {
                    await config.onNoCards();
                } catch (err) {
                    console.warn('onNoCards handler failed:', err);
                }
            } else {
                // Check if banner should always be visible - if so, swap to empty content
                // Otherwise, hide banner (will respect hierarchy rules)
                await hideCardsBanner();
            }
            return;
        }

        const bannerHtml = await (config.createCarouselHtml
            ? config.createCarouselHtml(cards)
            : createCardsCarousel(cards, config.carouselId));

        if (!bannerHtml) {
            logCards('No banner HTML generated, hiding banner');
            await hideCardsBanner();
            return;
        }

        const bannerDisplay = cardsSettings.BANNER_DISPLAY || {
            SHOW_TITLE: true,
            SHOW_CLOSE_BUTTON: true,
            SHOW_BADGE: true
        };

        window.currentBannerDisplay = bannerDisplay;

        if (config.createBadgeUpdateFunction) {
            window.updateCardBadge = config.createBadgeUpdateFunction(bannerDisplay);
        } else {
            window.updateCardBadge = createBadgeUpdateFunction(bannerDisplay);
        }

        const badgeResult = config.getInitialBadgeHtml
            ? await config.getInitialBadgeHtml(cards, bannerDisplay)
            : computeInitialBadgeHtml(cards, `cards_last_index_${config.carouselId}`, bannerDisplay);

        // Handle both old format (string) and new format (object with html and badgeKey)
        const badgeHtml = typeof badgeResult === 'string' ? badgeResult : badgeResult.html;
        const initialBadgeKey = typeof badgeResult === 'object' && badgeResult.badgeKey ? badgeResult.badgeKey : null;

        const tabsHtml = config.tabsHtml
            ? config.tabsHtml
            : (config.tabs
                ? renderTabs(config.tabs, config.tabsActiveId, config.tabsOnclick || '__cardsTabSelect', config.headerLeftSuffixHtml || '')
                : '');

        // Build action buttons HTML if spotActionButtons provided
        let actionButtonsHtml = '';
        if (config.spotActionButtons) {
            const { spotId, title, lat, lng, safeTitle, hasFavorite, hasDirections, hasShare } = config.spotActionButtons;
            const isFav = spotId ? isFavorite(spotId) : false;
            const favClass = isFav ? ' favorited' : '';

            const actionButtons = [
                hasFavorite ? `<button class="cards-action-btn cards-action-star${favClass}" onclick="window.__spotCardToggleFavorite('${spotId}', this)" title="Favorite" aria-label="Add to favorites">${createIconMarkup('star-solid-full', 'icon')}</button>` : '',
                hasDirections ? `<button class="cards-action-btn cards-action-directions" onclick="window.__spotCardDirections(${lat}, ${lng})" title="Directions" aria-label="Get directions">${createIconMarkup('diamond-turn-right-solid', 'icon')}</button>` : '',
                hasShare ? (spotId ? `<button class="cards-action-btn cards-action-share" onclick="window.__spotCardShare('${spotId}', '${safeTitle}')" title="Share" aria-label="Share">${createIconMarkup('share-nodes-solid', 'icon')}</button>` : `<button class="cards-action-btn cards-action-share" onclick="window.__tagCardShare()" title="Share" aria-label="Share">${createIconMarkup('share-nodes-solid', 'icon')}</button>`) : ''
            ].filter(Boolean).join('');

            if (actionButtons) {
                actionButtonsHtml = `<div class="cards-action-buttons">${actionButtons}</div>`;
            }
        }

        const containerHtml = config.buildContainerHtml
            ? config.buildContainerHtml(bannerHtml, badgeHtml, bannerDisplay, config.badgeRowContent || '', tabsHtml, initialBadgeKey, actionButtonsHtml, config.headerLeftSuffixHtml || '')
            : buildContainerHtml(bannerHtml, badgeHtml, bannerDisplay, config.badgeRowContent || '', tabsHtml, initialBadgeKey, actionButtonsHtml, config.headerLeftSuffixHtml || '');

        const applyContent = async () => {
            let finalHtml = containerHtml;

            // Inject image strip ABOVE banner if spotDetailCard provided
            if (config.spotDetailCard && config.spotDetailCard.images && config.spotDetailCard.images.length > 0) {
                const { renderSpotImageStrip } = await import('../marker/MarkerCards.js');
                const imageStripHtml = renderSpotImageStrip(config.spotDetailCard);
                if (imageStripHtml) {
                    finalHtml = imageStripHtml + containerHtml;
                }
            }

            container.innerHTML = finalHtml;
            activeCarouselId = config.carouselId;

            const state = carouselState.get(config.carouselId);
            if (state && state.cards && state.cards[state.currentIndex] && window.updateCardBadge) {
                window.updateCardBadge(state.cards[state.currentIndex]);
            }

            // Always setup gestures when content is applied, even if they were previously cleared
            setupCarouselGestures(config.carouselId);
            setupSwipeHintDismissal(config.carouselId);

            // After content applied, update header distance if placeholders exist and location is available
            try {
                const userLoc = typeof window !== 'undefined' ? window.userLocation : null;
                if (userLoc) {
                    const nodes = container.querySelectorAll('.cards-header-distance[data-lat][data-lng]');
                    nodes.forEach(async (node) => {
                        const lat = parseFloat(node.getAttribute('data-lat'));
                        const lng = parseFloat(node.getAttribute('data-lng'));
                        if (!isNaN(lat) && !isNaN(lng)) {
                            const meters = getDistanceMeters(userLoc.latitude, userLoc.longitude, lat, lng);
                            const dist = formatDistance(meters);
                            let tpl = '';
                            try { tpl = await getText('cards.distanceFromYou'); } catch (e) { }
                            const safeTpl = tpl || '{distance}';
                            const parts = safeTpl.split('{distance}');
                            const rawPrefix = parts[0] || '';
                            const rawSuffix = parts[1] || '';
                            const cleanedPrefix = rawPrefix.replace(/^[\s~≈∼]+/g, '');
                            const cleanedSuffix = rawSuffix.replace(/^[\s~≈∼]+/g, '');
                            const prefix = cleanedPrefix.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                            const suffix = cleanedSuffix.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                            const value = (dist || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                            const labelHtml = `<span class="distance-prefix">${prefix}</span><span class="distance-value">${value}</span><span class="distance-suffix">${suffix}</span>`;
                            const separatorIcon = createIconMarkup('distance', 'cards-distance-icon');
                            node.innerHTML = `<span class="cards-header-distance-separator">${separatorIcon}</span>${labelHtml}`;
                        }
                    });
                }
            } catch (e) {
                if (LOGGING?.ENABLED && LOGGING?.GENERAL) console.warn('[CARDS] Failed to update header distance after render:', e);
            }
        };

        if (isVisible) {
            if (config.animate) {
                // Wait for the fade out that started at the beginning to complete
                await fadeOutPromise;

                // Apply new content while invisible
                await applyContent();

                // Trigger reflow to ensure the hidden state is registered
                void container.offsetHeight;

                // Fade in new content
                container.classList.add('show');
                // Dispatch event for control visibility management
                window.dispatchEvent(new Event('cardBannerShow'));

                // Clear duration after animation completes
                setTimeout(() => {
                    if (container) container.style.transitionDuration = '';
                    // Resume marker listeners after transition completes
                    resumeMarkerListeners();
                }, duration + 50);
            } else {
                // INSTANT SWAP: Just update content (as it was before)
                await applyContent();
                // Resume markers immediately for instant swap
                resumeMarkerListeners();
            }
        } else {
            // FIRST LOAD: Apply and show immediately
            await applyContent();

            // Ensure transition duration is set
            container.style.transitionDuration = duration + 'ms';

            // Show banner immediately - no delays (container always in DOM, just show via CSS)
            void container.offsetHeight;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    container.classList.add('show');
                    // Dispatch event for control visibility management
                    window.dispatchEvent(new Event('cardBannerShow'));

                    // Clear duration after animation completes
                    setTimeout(() => {
                        if (container) container.style.transitionDuration = '';
                        // Resume marker listeners after transition completes
                        resumeMarkerListeners();
                    }, duration + 50);
                });
            });
        }

        logCards('Cards banner shown');

        if (config.trackImpression && cards.length > 0) {
            config.trackImpression(cards[0]);
        }

    } catch (error) {
        console.error('Error showing cards banner:', error);
        await hideCardsBanner();
    }
}
